<template>
  <div class="toolbar bg-gray-800 dark:bg-gray-900 text-white py-2 px-3 flex flex-col md:flex-row justify-between items-center gap-2 md:gap-0">
    <!-- Left section -->
    <div class="flex gap-2 sized-block">
      <div class="flex flex-col gap-2">
        <div v-if="pageSelection">
          <TextInput 
            style="margin-bottom: 0px; background-color: transparent"
            :property="themeNameProperty"
            :element="themeName"
            :container="null"
            :eventOnChangeRessource="true"
            @eventOnChangeRessource="setThemeName"
          />
        </div>
        <div class="flex items-center space-x-2">
          <button
            @click="undo()"
            :disabled="!canUndo"
            class="toolbar-btn"
            :class="{ 'opacity-50 cursor-not-allowed': !canUndo }"
          >
            <undoIcon />
          </button>
          <button
            @click="redo()"
            :disabled="!canRedo"
            class="toolbar-btn"
            :class="{ 'opacity-50 cursor-not-allowed': !canRedo }"
          >
            <redoIcon />
          </button>
        </div>
      </div>
      <div v-if="pageSelection">
        <PathInput style="margin-bottom: 0px; background-color: transparent; height: 100%; width: 100px;" :property="thumbnailProperty" :element="themeThumbnailPath" :container="null" :eventOnChangeRessource="true" @eventOnChangeRessource="changeThumbnailRessource" />
      </div>
    </div>

    <!-- Center section -->
    <div v-if="pageSelection" class="flex items-center" style="gap: 50px">
      <div>
        <SelectInput 
          style="margin-bottom: 0px; background-color: transparent; min-width: 225px;"
          :property="selectProperty"
          :element="currentPage"
          :container="null"
          :eventOnChangeRessource="true"
          @eventOnChangeRessource="setCurrentPage($event.value)"
        />
      </div>

    </div>
    <div v-else class="flex items-center gap-2">
      <button class="toolbar-btn btn-text" @click="returnToEditor()" v-tippy="'Sauvegarder et retourner à l\'éditeur'">
        <saveIcon />Sauvegarder & quitter<cancelIcon />
      </button>
    </div>

    <!-- Right section -->
    <div class="flex justify-end gap-10 sized-block">
      <div class="flex flex-col items-center gap-2" v-if="pageSelection">
        <button class="toolbar-btn" @click="checkForSaveTheme()" v-tippy="'Sauvegarder le thème'">
          <saveIcon />
        </button>
        <button 
          @click="handleExportTheme()" 
          class="toolbar-btn"
          v-tippy="'Télécharger le fichier du thème'"
        >
          <downloadIcon />
        </button>
      </div>
      <div class="flex flex-col gap-2" v-if="pageSelection">
        <button class="toolbar-btn" @click="quitEditor()" v-tippy="'Quitter l\'éditeur'">
          <logoutIcon />
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapMutations, mapActions } from 'vuex';
import * as mutates from "../vuexStore/mutations.js"
import * as actions from "../vuexStore/actions.js"
import * as namespaceStore from "../vuexStore/namespaceStore.js"
import defaultPage from "../vuexStore/defaultPage.js"
import PathInput from "./PropertyComponents/path.vue"
import TextInput from "./PropertyComponents/text.vue"
import SelectInput from "./PropertyComponents/select.vue"
import { Tippy } from 'vue-tippy'
import downloadIcon from './IconComponents/downloadIcon.vue'
import saveIcon from './IconComponents/saveIcon.vue'
import undoIcon from './IconComponents/undoIcon.vue'
import redoIcon from './IconComponents/redoIcon.vue'
import cancelIcon from './IconComponents/cancelIcon.vue'
import logoutIcon from './IconComponents/logoutIcon.vue'

export default {
  name: 'Toolbar',
  props: {
  },
  data() {
    return {
      thumbnailProperty: { name:"Path", text: "Miniature", type: "pathInput", dataType: "string", ressourceType: "image" },
      themeNameProperty: { name:"Text", text: "Nom du thème", type: "textInput", dataType: "string" },
      alert: {
        show: false,
        message: '',
        type: ''
      }
    }
  },
  components: {
    Tippy,
    PathInput,
    TextInput,
    SelectInput,
    downloadIcon,
    saveIcon,
    undoIcon,
    redoIcon,
    cancelIcon,
    logoutIcon
  },
  emits: [
    'show-resource-manager',
    'file-upload',
    'export-theme',
    'preview'
  ],
  props: {
    pageSelection: {
      type: Boolean,
      default: false
    }
  },
  watch: {
  },
  computed: {
    ...mapGetters(namespaceStore.THEME, [
      'canUndo',
      'canRedo',
      'currentPageIndex',
      'pages',
      'pagesArray',
      'snapData',
      'previewIsVisible',
      'exportTheme',
      'compositionEditing',
      'compositions',
      'themeName',
      'themeId',
      'themeDescription',
      'themeJson',
      'themeThumbnailPath',
      'themeThumbnail',
      'themeModificationNotSaved',
      'checkThemeIsComplete'
    ]),
    pageOptions: function() {
      return Object.entries(defaultPage).map(page => 
        {
          return { name: page[0], value: page[0], idSoft: page[1].idSoft, text: page[1].text };
        }
      );
    },
    selectProperty: function() {
      return { 
        name:"Select",
        text: "Edition de la page",
        type: "selectInput",
        multiple: false,
        dataType: "string",
        optionsType: "idSoft",
        options: this.pageOptions
      }
    },
    currentPage: function() {
      return this.pageOptions.find(page => page.name == this.currentPageIndex)?.idSoft;
    }
  },
  methods: {
    ...mapMutations({
      undo: namespaceStore.THEME + "/" + mutates.UNDO,
      redo: namespaceStore.THEME + "/" + mutates.REDO,
      setSnapEnabled: namespaceStore.THEME + "/" + mutates.SET_SNAP_ENABLED,
      saveCompositionEdit: namespaceStore.THEME + "/" + mutates.SAVE_COMPOSITION_EDIT,
      addAlert: namespaceStore.MESSAGE + "/" + mutates.ADD_ALERT,
      removeComposition: namespaceStore.THEME + "/" + mutates.REMOVE_COMPOSITION,
      setName: namespaceStore.THEME + "/" + mutates.SET_THEME_NAME,
      setDescription: namespaceStore.THEME + "/" + mutates.SET_THEME_DESCRIPTION,
      setThemeThumbnail: namespaceStore.THEME + "/" + mutates.SET_THEME_THUMBNAIL
    }),
    ...mapActions(namespaceStore.THEME, {
      uploadComposition: actions.UPLOAD_COMPOSITION,
      setCurrentPage: actions.SET_CURRENT_PAGE,
      uploadTheme: actions.UPLOAD_THEME,
      exportFullTheme: actions.EXPORT_FULL_THEME
    }),
    ...mapActions(namespaceStore.MESSAGE, [
      "addMessage",
      "addAlert"
    ]),
    confirmQuitEditor() {
      this.$router.push({name: 'ThemeSelector'});
    },
    quitEditor() {
      if (this.themeModificationNotSaved) {
        let resolver = null;
        let rejecter = null;
        let promiseInfo = {
          promise : new Promise((resolve, reject) => {
            resolver = resolve;
            rejecter = reject;
          }),
          resolver: resolver,
          rejecter: rejecter
        }
        this.addAlert({
          title: "Attention",
          validateMessage: "Quitter",
          cancelMessage: "Annuler",
          message: [
            { text: 'Certaines modifications n\'ont pas été sauvegardées.', style: {"fontWeight": "normal", "fontSize": "1.2rem"} },
            { text: 'Etes-vous sûr de vouloir quitter ?', style: {"fontWeight": "normal", "fontSize": "1.2rem"} },
          ],
          promise: promiseInfo
        });
        promiseInfo.promise.then((result) => {
          this.confirmQuitEditor();
        });
        return;
      }
      else {
        this.confirmQuitEditor();
      }
    },
    changeThumbnailRessource(ressource) {
      if (ressource) {
        this.setThemeThumbnail(ressource.id);
      }
    },
    setThemeName(name) {
      this.setName(name);
    },
    setThemeDescription(description) {
      this.setDescription(description);
    },
    checkTheme(callback_Ok, callback_Ko) {
      const check = this.checkThemeIsComplete;
      if (check.isComplete == false) {
        let resolver = null;
        let promiseInfo = {
          promise : new Promise((resolve, reject) => {
            resolver = resolve;
          }),
          resolver: resolver,
        };
        this.addAlert({
          title: "Attention",
          validateMessage: "Corriger",
          message: [
            { text: "Certains éléments indispensables au thème sont manquants ou non définis.", style: {"fontWeight": "normal", "fontSize": "1.2rem"} },
            { text: "Veuillez définir ou ajouter les éléments suivants avant de sauvegarder :", style: {"fontWeight": "normal", "fontSize": "1.2rem", "marginBottom": "25px"} },
            ...check.missingElements.map(el => ({ text: el, style: {"fontWeight": "bold", "fontSize": "1.2rem", "textDecoration": "underline"} }))
          ],
          promise: promiseInfo
        });
      }
      else {
        callback_Ok();
      }
    },
    checkCompositionIsComplete(callback_Ok, callback_Ko) {
      const check = this.compositionEditing.checkIsComplete();
      if (check.isComplete == false) {
        let resolver = null;
        let rejecter = null;
        let promiseInfo = {
          promise : new Promise((resolve, reject) => {
            resolver = resolve;
            rejecter = reject;
          }),
          resolver: resolver,
          rejecter: rejecter
        }
        this.addAlert({
          title: "Attention",
          validateMessage: "Corriger",
          cancelMessage: check.requiredBeforeSave == true ? "Supprimer la composition" : "Ignorer",
          message: [
            { text: "Certains éléments sont manquants ou non définis.", style: {"fontWeight": "normal", "fontSize": "1.2rem"} },
            { text: "Veuillez définir ou ajouter les éléments suivants :", style: {"fontWeight": "normal", "fontSize": "1.2rem", "marginBottom": "25px"} },
            ...check.missingElements.map(el => ({ text: el, style: {"fontWeight": "bold", "fontSize": "1.2rem", "textDecoration": "underline"} }))
          ],
          promise: promiseInfo
        });
        promiseInfo.promise.catch(() => {
          if (check.requiredBeforeSave == true) {
            callback_Ko();
          }
          else {
            callback_Ok();
          }
        });
      }
      else {
        callback_Ok();
      }
    },
    saveComposition(composition) {
      let formData = new FormData();
      formData.append('json_data', composition.toJSON());
      if (composition.Name)
        formData.append('nom', composition.Name);
      if (composition.description)
        formData.append('description', composition.description);
      if (composition.siteId)
        formData.append('id', composition.siteId);
      return this.uploadComposition(formData).then(() => {
        this.addMessage({
          message: 'Composition sauvegardée avec succès : ' + composition.Name,
          type: 'success'
        });
      }).catch((error) => {
        this.addMessage({
          message: 'Erreur lors de l\'upload de la composition : ' + composition.Name,
          type: 'error'
        });
      });
    },
    saveTheme() {
      let formData = new FormData();
      formData.append('json_data', JSON.stringify(this.themeJson));
      if (this.themeName)
        formData.append('nom', this.themeName);
      else {
        this.addMessage({
          message: 'Veuillez entrer un nom pour le thème avant de sauvegarder',
          type: 'error'
        });
        return;
      }
      if (this.themeDescription)
        formData.append('description', this.themeDescription);
      if (this.themeId)
        formData.append('id', this.themeId);
      if (this.themeThumbnail)
        formData.append('thumbnail', this.themeThumbnail);
      else {
        this.addMessage({
          message: 'Veuillez entrer une miniature pour le thème avant de sauvegarder',
          type: 'error'
        });
        return;
      }
      return this.uploadTheme(formData).then((result) => {
        let promises = [];
        this.compositions.forEach(composition => {
          promises.push(this.saveComposition(composition));
        });
        Promise.all(promises).then(() => {
          this.addMessage({
            message: 'Thème sauvegardé avec succès',
            type: 'success'
          });
        });
      }).catch((error) => {
        console.error('Erreur lors de l\'upload du thème : ' + this.themeName, error);
        this.addMessage({
          message: 'Erreur lors de l\'upload du thème : ' + this.themeName,
          type: 'error'
        });
      });
    },
    checkForSaveTheme() {
      this.checkTheme(() => {
          this.saveTheme();
        },
        () => {}
      );
    },
    returnToEditor() {
      this.checkCompositionIsComplete(() => {
          this.saveComposition(this.compositionEditing).then(() => {
            this.$router.push({name: 'Editor'});
          });
        },
        () => {
          this.removeComposition(this.compositionEditing);
          this.$router.push({name: 'Editor'});
        }
      );
    },
    handleExportTheme() {
      try {
        if (!this.themeId || this.themeModificationNotSaved == true) {
          this.addMessage({
            message: 'Veuillez sauvegarder le thème avant de l\'exporter',
            type: 'error'
          });
          return;
        }
        if (!this.themeName) {
          this.addMessage({
            message: 'Veuillez entrer un nom pour le thème avant de l\'exporter',
            type: 'error'
          });
          return;
        }
        if (!this.themeThumbnail) {
          this.addMessage({
            message: 'Veuillez entrer une miniature pour le thème avant de l\'exporter',
            type: 'error'
          });
          return;
        }
        this.exportFullTheme();
      } catch (error) {
        console.error('Erreur lors de l\'exportation du thème:', error);
        alert(`Erreur lors de l'exportation: ${error.message}`);
      }
    },
  }
}
</script>

<style scoped>
.toolbar {
  @apply shadow-md;
  margin: 10px;
  border: 1px solid #a4a4a4;
  border-radius: 5px;
}

.toolbar-btn {
  width: 40px;
  height: 40px;
  display: flex;
  justify-content: center;
  align-items: center;
  @apply rounded hover:bg-gray-700 dark:hover:bg-gray-600 transition-colors;
  flex-direction: row;
  gap: 10px;
  padding: 0;
}

.toolbar-btn.btn-text {
  width: auto;
  color: black;
  padding: 0 10px;
}

.toolbar-btn.btn-text:hover {
  color: white;
}

.toolbar-btn svg:not(.no-color) {
  color: black;
}

.toolbar-btn:hover svg:not(.no-color) {
  color: white;
}

.sized-block {
  flex: 1;
}
</style> 